// ============================================================================
// EVENT CARD - Individual event card displayed in the calendar grid
// ============================================================================
import React from 'react';
import { getEventTypeColor } from './constants';
import { formatTimeShort } from './utils';
import { isErrorAcknowledgedAnywhere, inferErrorCategory } from '../../lib/validationHelpers';

export default function EventCard({
  event,
  eventTypes,
  onClick,
  acknowledgedPatterns = [],
  errorFocus = false
}) {
  const eventTypeName = event.type || event.event_type;
  const eventTypeData = eventTypes.find(et => et.name === eventTypeName);
  const displayName = eventTypeData?.display_name || eventTypeName || 'Event';

  // Calculate spots remaining (sum across grouped variants if applicable)
  const isFull = event.has_openings === false;
  let totalOpenings = null;
  if (event.isGrouped && Array.isArray(event.groupedEvents)) {
    const variantsWithData = event.groupedEvents.filter(v => v.openings != null);
    if (variantsWithData.length > 0) {
      totalOpenings = variantsWithData.reduce((sum, v) => sum + (v.openings || 0), 0);
    }
  } else if (event.openings != null) {
    totalOpenings = event.openings;
  }
  const isLow = totalOpenings != null && totalOpenings > 0 && totalOpenings <= 3;
  const showCount = totalOpenings != null && !isFull;
  const countTooltip = event.isGrouped
    ? `${totalOpenings} spots open across ${event.optionCount} options`
    : `${totalOpenings} spots open`;

  // What counts as "needs attention" on the CALENDAR — data errors or a
  // missing/flyer-only description. The AI review lives in the Errors tab,
  // NOT on the calendar (Jayme, July 2). Drives the corner dots + Errors Focus.
  const activeErrors = (event.validation_errors || []).filter(
    err => err.type !== 'sold_out' && !isErrorAcknowledgedAnywhere(event, err.message, acknowledgedPatterns)
  );
  const dataErrors = activeErrors.filter(err => inferErrorCategory(err) === 'data_error');
  const hasDescIssue = event.description_status === 'none' || event.description_status === 'flyer_only';
  const hasIssue = dataErrors.length > 0 || hasDescIssue;
  // The single line we surface on the card in Errors Focus mode.
  const focusReason = dataErrors[0]?.message
    || (event.description_status === 'none' ? 'No description'
        : event.description_status === 'flyer_only' ? 'Flyer only — no text' : null);

  // Get validation status indicators — ONLY shown in Errors Focus mode.
  // Normal calendar view = events + spots, no error dots (Jayme).
  const getValidationIndicator = () => {
    if (!errorFocus) return null;
    const hasDataErrors = dataErrors.length > 0;

    // Show colored dot for data errors
    if (hasDataErrors) {
      return (
        <span className="absolute -top-1 -right-1" title={`${dataErrors.length} DATA ERROR(S) - wrong info!`}>
          <span className="w-3 h-3 bg-red-500 rounded-full shadow-sm inline-block border border-red-700"></span>
        </span>
      );
    } else if (event.description_status === 'flyer_only') {
      // Flyer only - small gray dot
      return (
        <span className="absolute -top-1 -right-1" title="Has flyer but no text description">
          <span className="w-2 h-2 bg-gray-400 rounded-full shadow-sm inline-block"></span>
        </span>
      );
    } else if (event.description_status === 'none') {
      // No description - hollow red circle
      return (
        <span className="absolute -top-1 -right-1" title="No description at all">
          <span className="w-2.5 h-2.5 border-2 border-red-500 rounded-full inline-block bg-white"></span>
        </span>
      );
    }

    return null;
  };

  // Build hover tooltip with the info we removed from the card
  const eventTime = formatTimeShort(event.time || event.event_time);
  const tooltipParts = [event.title || displayName];
  if (eventTime) tooltipParts.push(`Time: ${eventTime}`);
  if (showCount) tooltipParts.push(`${totalOpenings} spots open`);
  if (event.isGrouped) tooltipParts.push(`${event.optionCount} options`);
  if (event.has_openings === false) tooltipParts.push('SOLD OUT');
  if (event.not_on_portal) tooltipParts.unshift('NOT ON PORTAL - built in iClass, parents cannot see it');
  const cardTooltip = tooltipParts.join(' · ');
  // Built in iClass but hidden from the portal: faded + dashed + tagged (Jayme 2026-11-04).
  const notOnPortal = !!event.not_on_portal;

  // Errors Focus mode: clean events fade back, issue events pop with a red ring.
  const dimmed = errorFocus && !hasIssue;
  const spotlit = errorFocus && hasIssue;

  return (
    <div
      className="relative group cursor-pointer transition-all duration-200"
      onClick={() => onClick(event)}
      title={cardTooltip}
      style={dimmed ? { opacity: 0.25, filter: 'grayscale(1)' } : notOnPortal ? { opacity: 0.55 } : {}}
    >
      <div
        className="text-xs rounded text-gray-700 text-center font-medium transition-all duration-200 border p-2 hover:shadow-md hover:scale-105 relative"
        style={{
          backgroundColor: getEventTypeColor(eventTypeName, eventTypes),
          borderColor: spotlit ? '#dc2626' : notOnPortal ? '#4b3f40' : 'rgba(0,0,0,0.1)',
          ...(notOnPortal ? { borderStyle: 'dashed', borderWidth: 2 } : {}),
          ...(spotlit ? { boxShadow: '0 0 0 2px #dc2626, 0 3px 10px rgba(220,38,38,.3)' } : {})
        }}
      >
        {/* SOLD OUT badge - top left */}
        {event.has_openings === false && (
          <span className="absolute -top-1 -left-1 bg-red-600 text-white text-[8px] font-bold px-1 rounded" title="SOLD OUT - no spots available">
            FULL
          </span>
        )}

        {notOnPortal && (
          <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1 font-black"
            style={{ background: '#2b2224', color: '#ffffff', fontSize: 8, lineHeight: '12px' }}>
            NOT ON PORTAL
          </span>
        )}

        {/* Validation status indicator */}
        {getValidationIndicator()}

        {/* Compact Card View — TYPE name */}
        <div className="font-semibold leading-tight text-sm">
          {displayName}
        </div>
        {/* Options count for grouped events (single events skip this row entirely) */}
        {event.isGrouped && (
          <div className="text-gray-500 italic mt-0.5 leading-tight text-xs">
            {event.optionCount} opts
          </div>
        )}
        {/* Errors Focus: show WHY it's flagged instead of the spot count. */}
        {spotlit && focusReason ? (
          <div className="mt-0.5 leading-tight text-[10px] font-semibold text-red-700 line-clamp-2">
            {focusReason}
          </div>
        ) : (
          /* Openings count line (normal mode) */
          showCount && (
            <div
              className={`mt-0.5 leading-tight text-xs font-semibold ${isLow ? 'text-orange-700' : 'text-green-700'}`}
            >
              {isLow ? '⚠️' : '🟢'} {totalOpenings}
            </div>
          )
        )}
      </div>
    </div>
  );
}
